import type { LucideIcon } from 'lucide-react'
import type { ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { cn } from '../../lib/utils'
import { Button, type ButtonProps } from './button'
import { Tooltip } from './tooltip'

type TableIconActionsProps = {
  children: ReactNode
  className?: string
}

/** One-line row of icon actions for listing tables. Use on all listing pages. */
export function TableIconActions({ children, className }: TableIconActionsProps) {
  return (
    <div className={cn('flex flex-nowrap items-center gap-1', className)}>{children}</div>
  )
}

type IconActionButtonProps = {
  icon: LucideIcon
  label: string
  onClick?: () => void
  to?: string
  variant?: ButtonProps['variant']
  disabled?: boolean
  className?: string
}

/**
 * Compact icon-only action button for listing tables.
 * Always pass `label` for tooltip + screen readers.
 */
export function IconActionButton({
  icon: Icon,
  label,
  onClick,
  to,
  variant = 'secondary',
  disabled = false,
  className,
}: IconActionButtonProps) {
  const icon = <Icon className="h-4 w-4" aria-hidden />

  // Disabled linked actions render as a button (no navigation) so the icon stays visible.
  if (to && !disabled) {
    return (
      <Tooltip label={label}>
        <Button asChild size="iconSm" variant={variant} className={className}>
          <Link to={to} aria-label={label}>
            {icon}
          </Link>
        </Button>
      </Tooltip>
    )
  }

  return (
    <Tooltip label={label}>
      <Button
        type="button"
        size="iconSm"
        variant={variant}
        className={className}
        aria-label={label}
        disabled={disabled}
        onClick={onClick}
      >
        {icon}
      </Button>
    </Tooltip>
  )
}
